// src/utils/checklistClient.js
// chklstweb-3a7f1c58 — CHECKLISTS SEAM 3 (web job pop-up).
//
// The web's one door to the Seam 1 checklist callables (chklst-7e4b2a96), plus the
// small client mirrors the panel needs to decide what to SHOW. Every decision that
// matters is the server's: functions/utils/checklists.js validates, and firestore.rules
// keeps checklists, checklistTemplates and jobChecklistState server-write-only. A
// button this file wrongly shows is simply refused, with the server's own sentence.
//
// Staff-only by construction: none of these paths has a portal clause in the rules.

import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { ROLES, HIGH_LEVEL_ROLES, WORK_PERFORMED_EDITOR_ROLES } from './permissions';

// ─── callables ──────────────────────────────────────────────────────────────
const call = (name) => async (payload) => {
    const res = await httpsCallable(functions, name)(payload);
    return res?.data || {};
};
export const attachChecklist = call('attachChecklistToJob');           // { jobId, templateId }
export const removeChecklist = call('removeChecklistFromJob');         // { jobId, checklistId, reason? } — reason for a blocking, unfinished one (clremove-3f7d1a85)
export const saveChecklistAnswers = call('saveChecklistAnswers');      // { jobId, checklistId, answers, notes? } — notes: chklstmulti-3e9b7c15
export const ensureChecklistMediaNote = call('ensureChecklistMediaNote'); // { jobId, checklistId, questionId }
export const hideChecklistMedia = call('hideChecklistMedia');          // { jobId, checklistId, questionId, mediaId } — chklsthide-4f8b2d61: hides, never deletes
export const restoreChecklistMedia = call('restoreChecklistMedia');    // same payload — Owner/Admin/Manager; removal stays on record
export const overrideChecklistGate = call('overrideChecklistGate');    // { jobId, reason }
export const setChecklistsLocked = call('setChecklistsLocked');        // { jobId, locked }
// chklstbuild-6c1e9b42 — Seam 6 builder (Settings › Checklists). Owner / Admin / Manager.
export const saveChecklistTemplate = call('saveChecklistTemplate');    // { templateId?, expectedVersion (0 = new), name, sections, autoAttach, blocksCompletion }
export const setChecklistTemplateArchived = call('setChecklistTemplateArchived'); // { templateId, archived }
// custcopy-32471cbd — Seam 8b: the customer copy as a PDF. { jobId, checklistId } → { pdfBase64, fileName, bytes,
// smallerPhotos }: the file itself (≤ 10 MB as base64); nothing is stored and there is no link. Office roles;
// the server re-checks.
export const getChecklistCopyPdf = call('getChecklistCopyPdf');

// custcopy-32471cbd — Seam 8c: Send to customer (office roles; every rule is the server's —
// functions/utils/checklistCopySend.js). Making the PDF + emailing can take a while: 2-minute timeout.
const callSlow = (name) => async (payload) => {
    const res = await httpsCallable(functions, name, { timeout: 120000 })(payload);
    return res?.data || {};
};
export const sendChecklistCopy = callSlow('sendChecklistCopy');   // { jobId, checklistId, to, subject, message, sendUnfinished? } → { sendId, status }
export const getChecklistSentPdf = call('getChecklistSentPdf');    // { sendId } → { pdfBase64, fileName }: the exact PDF that was emailed
// Mirrors of the server's limits (display only).
export const SEND_SUBJECT_MAX = 200;
export const SEND_MESSAGE_MAX = 2000;
export const defaultChecklistSubject = (name, jobNumber) =>
    (jobNumber ? `Your ${name || 'checklist'} — Job #${jobNumber}` : `Your ${name || 'checklist'}`);
// The same question everywhere a checklist that isn't finished is about to be sent (8c dialog, 8d tick).
export const CHECKLIST_UNFINISHED_ASK = "This checklist isn't finished — send anyway?";

// custcopy-32471cbd 8d — what the invoice send banner offers: the job's checklists that are not
// removed and whose CURRENT template has "Customers can receive a copy" on, in the order they were
// added. Display only: sendInvoiceEmail re-checks every tick from the database.
const addedMs = (c) => (c.addedAt?.toMillis ? c.addedAt.toMillis() : 0);
export const invoiceChecklistChoices = (copies, templates) => {
    const on = new Set((templates || []).filter(t => t && t.customerCopy === true).map(t => t.id));
    return (copies || [])
        .filter(c => c && c.removed !== true && on.has(c.templateId))
        .sort((a, b) => addedMs(a) - addedMs(b))
        .map(c => ({ id: c.id, name: c.name || 'Checklist', status: c.status || 'not_started' }));
};

// One address, nothing else (the server refuses lists, names and header tricks).
export const isOneEmailAddress = (v) => typeof v === 'string' && /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[^\s@<>"',;]+$/.test(v.trim());

// clfix-56ea256b M2 — MIRROR of functions/utils/checklistSafety.js emailsOnFile / canTypeAnyAddress (the
// server decides; this only shapes the To field). Office Staff send only to an address on file: the
// customer's email or a property's contact email. Owner / Admin / Manager may type any address.
export const canSendToAnyAddress = (profile) => !!profile?.role && HIGH_LEVEL_ROLES.includes(profile.role);
export const checklistEmailsOnFile = (customers) => {
    const out = [];
    const seen = new Set();
    const add = (e) => {
        const t = typeof e === 'string' ? e.trim() : '';
        if (!t || seen.has(t.toLowerCase())) return;
        seen.add(t.toLowerCase());
        out.push(t);
    };
    for (const c of customers || []) {
        if (!c) continue;
        add(c.email);
        for (const p of Array.isArray(c.properties) ? c.properties : []) if (p) add(p.contactEmail);
    }
    return out;
};
export const NO_EMAIL_ON_FILE = "This customer has no email on file. Add one to the customer (or one of their properties), or ask an Owner, Admin or Manager to send it.";

// custcopy-32471cbd — PDF bytes from the server → a downloaded file (no tab, no link: the file exists only
// in this browser). Print / PDF, Preview and "View what was sent" all use it.
// docpdf-8a4c1e73 — moved to utils/pdfDownload.js (every PDF in the app uses it now); re-exported here.
export { downloadPdfBytes } from './pdfDownload';

// chklstnew-5a8c2e71 — Seam 6c: the checklists picked on the NEW-job form, attached once the
// job exists on the server. One attachChecklistToJob per pick, in parallel; a failure never
// undoes the job. The server's duplicate check makes an overlap with auto-attach harmless.
// Returns the names that could NOT be added (empty = all added).
export const attachChecklistsToNewJob = async (jobId, picks) => {
    const results = await Promise.allSettled((picks || []).map(t => attachChecklist({ jobId, templateId: t.id })));
    return results
        .map((r, i) => (r.status === 'rejected' && r.reason?.code !== 'functions/already-exists' ? picks[i].name : null))
        .filter(Boolean);
};

// The one message when some picks didn't attach (the job itself is saved).
export const newJobChecklistFailureMessage = (names) => {
    const list = names.map(n => `'${n}'`).join(', ');
    return `Job saved, but ${list} couldn't be added. Open the job to add ${names.length === 1 ? 'it' : 'them'}.`;
};

// ─── mirrors of functions/utils/checklists.js ───────────────────────────────
// Change these together with the server module.
export const CHECKLIST_DONE_STATUSES = ['Completed', 'Canceled', 'Cancelled'];
export const OVERRIDE_REASON_MAX = 500;
const YESNO_VALUES = ['yes', 'no', 'na'];
export const MEDIA_QUESTION_TYPES = ['photos', 'signature'];
// chklstmulti-3e9b7c15 — mirrors of the server's LIMITS.answerNote and takesPhotos (attached half).
export const ANSWER_NOTE_MAX = 1000;
export const takesAttachedPhotos = (q) => !!q && q.allowPhotos === true && q.type !== 'info' && !MEDIA_QUESTION_TYPES.includes(q.type);

export const mediaNoteId = (checklistId, questionId) => `cl-${checklistId}-${questionId}`;

// chklsthide-4f8b2d61 — mirror of the server's isUsableMedia: a media doc counts and shows
// unless its upload failed or it was removed (hidden) through hideChecklistMedia.
export const isUsableChecklistMedia = (m) => !!m && m.uploadStatus !== 'failed' && m.hidden !== true;

// A media doc's remove / restore events, NEWEST FIRST: [{ action: 'hidden'|'restored', by, at, reason }].
// From the append-only mediaHistory; a doc removed before mediaHistory existed falls back to its
// single latest fields (an earlier restore, then the current removal). Ties keep list order.
// Same as the phone's checklistRules.js mediaEvents (tested there, R20).
const eventMs = (t) => (t && typeof t.toMillis === 'function' ? t.toMillis()
    : t instanceof Date ? t.getTime() : typeof t === 'number' ? t : 0);
export const mediaEvents = (m) => {
    if (!m) return [];
    let events;
    if (Array.isArray(m.mediaHistory) && m.mediaHistory.length) {
        events = m.mediaHistory
            .filter(e => e && (e.action === 'hidden' || e.action === 'restored'))
            .map(e => ({ action: e.action, by: e.by || null, at: e.at || null, reason: e.reason || null }));
    } else {
        events = [];
        if (m.restoredBy || m.restoredAt) events.push({ action: 'restored', by: m.restoredBy || null, at: m.restoredAt || null, reason: null });
        if (m.hiddenBy || m.hiddenAt) events.push({ action: 'hidden', by: m.hiddenBy || null, at: m.hiddenAt || null, reason: m.hiddenReason || null });
    }
    return events
        .map((e, i) => ({ e, i }))
        .sort((a, b) => (eventMs(b.e.at) - eventMs(a.e.at)) || (b.i - a.i))
        .map(({ e }) => e);
};

export const isChecklistJobDone = (job) => CHECKLIST_DONE_STATUSES.includes(job?.status);

// Answers are editable while the job is not done, or while the office has unlocked them.
export const isChecklistEditable = (job, state) =>
    !isChecklistJobDone(job) || !!(state && state.checklistsUnlocked === true);

const isRealDate = (s) => {
    if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

// Mirror of the server's isAnswered. Used ONLY to outline missing answers in red —
// the gate itself is always the server's count.
export const isChecklistAnswered = (question, value, hasMedia) => {
    if (MEDIA_QUESTION_TYPES.includes(question.type)) return !!hasMedia;
    if (value === undefined || value === null) return false;
    switch (question.type) {
        case 'short':
        case 'long': return typeof value === 'string' && value.trim() !== '';
        case 'yesno': return YESNO_VALUES.includes(value);
        case 'choice': return typeof value === 'string' && value !== '';
        case 'multi': return Array.isArray(value) && value.length > 0; // chklstmulti-3e9b7c15
        case 'checkbox': return value === true;
        case 'number': return typeof value === 'number' && Number.isFinite(value);
        case 'date': return isRealDate(value);
        default: return false;
    }
};

// ─── who sees which control (display only; the server is the gate) ─────────
// Built from permissions.js lists, NOT can(): can() misses the 'Owner' and
// 'System Admin' spellings that exist in production.
export const isChecklistOfficeRole = (profile) =>
    !!profile?.role && WORK_PERFORMED_EDITOR_ROLES.includes(profile.role);

export const canOverrideChecklist = (profile) =>
    !!profile?.role && HIGH_LEVEL_ROLES.includes(profile.role);

export const canLockChecklists = isChecklistOfficeRole;

// clremove-3f7d1a85 — mirror of checklists.js isBlockingUnfinished (the SERVER's counts). Removing one is
// Owner / Admin / Manager only, with a reason — canOverrideChecklist decides who sees the ×.
export const isChecklistBlockingUnfinished = (c) => !!c && c.blocksCompletion === true
    && (Number(c.requiredAnswered) || 0) < (Number(c.requiredTotal) || 0);

// chk7d-4c7e1a92 — the "Removed checklists" list: every soft-removed copy (required or not), newest removal
// first, with who / when / why. The reason is the copy's removedReason, else the job's removal record
// (state.checklistRemovals, written for required, unfinished removals). Display only.
const removedMs = (v) => (v?.toMillis ? v.toMillis() : (v instanceof Date ? v.getTime() : 0));
export const removedChecklistRows = (removed, state) => (removed || [])
    .filter(c => c && c.removed === true)
    .map(c => ({
        id: c.id,
        copy: c,
        name: c.name || 'Checklist',
        required: c.blocksCompletion === true,
        byName: c.removedBy?.name || state?.checklistRemovals?.[c.id]?.by?.name || null,
        at: c.removedAt || state?.checklistRemovals?.[c.id]?.at || null,
        reason: c.removedReason || state?.checklistRemovals?.[c.id]?.reason || null,
    }))
    .sort((a, b) => removedMs(b.at) - removedMs(a.at) || a.id.localeCompare(b.id));

const isAssignedTo = (job, uid) => !!uid && (
    (Array.isArray(job?.assignedTo) && job.assignedTo.includes(uid))
    || (Array.isArray(job?.assignedGroupMemberIds) && job.assignedGroupMemberIds.includes(uid)));

// Office Staff and above on any job; a Field Technician only on a job they are on.
// A Sales Consultant gets a read-only panel (the server's FILL_ROLES leaves them out).
export const canFillChecklists = (profile, job, uid) => {
    if (isChecklistOfficeRole(profile)) return true;
    return profile?.role === ROLES.TECH && isAssignedTo(job, uid || profile?.id);
};

// ─── the completion gate ────────────────────────────────────────────────────
export const CHECKLIST_BLOCKED_MESSAGE =
    'This job has a checklist that must be finished first. Finish it, or ask an Owner, Admin or Manager to complete it anyway.';

// One fresh read of the gate. No state doc = no checklists = 'clear'.
export const readChecklistGate = async (orgId, jobId) => {
    if (!orgId || !jobId) return 'clear';
    const snap = await getDoc(doc(db, `organizations/${orgId}/jobChecklistState/${jobId}`));
    return snap.exists() && (snap.data() || {}).checklistGate === 'blocked' ? 'blocked' : 'clear';
};

// A Completed write refused by firestore.rules arrives as a bare permission-denied.
// Returns the plain sentence when the checklist gate is the reason, otherwise null
// (the caller keeps its own message). Never throws.
export const explainChecklistRefusal = async ({ orgId, jobId, error }) => {
    if (error?.code !== 'permission-denied') return null;
    try {
        return (await readChecklistGate(orgId, jobId)) === 'blocked' ? CHECKLIST_BLOCKED_MESSAGE : null;
    } catch (_) {
        return null;
    }
};

// Blocking copies still missing required answers, for the "can't complete yet" dialog.
export const blockingChecklists = (checklists) => (checklists || [])
    .filter(c => c.removed !== true && c.blocksCompletion === true
        && (Number(c.requiredAnswered) || 0) < (Number(c.requiredTotal) || 0))
    .map(c => ({ ...c, missing: (Number(c.requiredTotal) || 0) - (Number(c.requiredAnswered) || 0) }));
